// FF-1129 (Analytics Outbound v1 — app GA4 light, ZEE port)
//
// App-level analytics for ff-dashboard-zee.
//
// The client POSTs a lightweight body { client_id, event_name, params } to the
// Main API (ff-backend, already Clerk-authenticated).  The backend holds the
// GA4 creds, stamps user_id + brand_id from the Clerk token, and forwards to
// GA4 MP.  No VITE_GA4_* env vars — secrets never enter the public bundle.
//
// Hardening:
//  - Consent gate: no POST when isAppTrackingSuppressed.
//  - Persistent client_id: resolved from localStorage once per instance.
//  - Sentry captureException on send failure (fire-and-forget).
//  - isLoaded gate: lives in app-analytics-init.tsx (not duplicated here).
//
// The fetcher is injectable so unit tests never touch the network.

import { captureException } from '@sentry/react';

import {
  AppConsentEnv,
  browserConsentEnv,
  isAppTrackingSuppressed,
} from './ga4-consent-gate';
import { buildBackendEventBody, generateClientId } from './ga4-event-builder';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Path on the Main API that proxies the event to GA4 MP. */
const APP_ANALYTICS_ENDPOINT = '/v1/app-analytics/event';

/** localStorage key for the persistent GA4 client_id. */
const CLIENT_ID_STORAGE_KEY = 'ff_ga4_cid';

// ---------------------------------------------------------------------------
// Persistent client_id
// ---------------------------------------------------------------------------

/** Minimal storage contract (localStorage-compatible); injectable for tests. */
export interface AppAnalyticsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Real localStorage when present; null under SSR or when access throws. */
const defaultStorage = (): AppAnalyticsStorage | null => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
};

/**
 * Resolve a stable GA4 client_id, persisted across reloads/sessions.
 * Without a stable cid GA4 treats every event as a new user/session.
 */
export const resolveClientId = (
  storage: AppAnalyticsStorage | null
): string => {
  if (!storage) return generateClientId();
  try {
    const existing = storage.getItem(CLIENT_ID_STORAGE_KEY);
    if (existing) return existing;
    const cid = generateClientId();
    storage.setItem(CLIENT_ID_STORAGE_KEY, cid);
    return cid;
  } catch {
    return generateClientId();
  }
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Options for createAppAnalytics.
 *
 * measurementId and apiSecret are intentionally ABSENT — GA4 creds live only
 * on the server.
 */
export interface AppAnalyticsOptions {
  /** Injectable consent environment — defaults to real browser globals. */
  consentEnv?: AppConsentEnv;
  /** Injectable client_id storage — defaults to localStorage. */
  storage?: AppAnalyticsStorage;
}

/**
 * Fetcher that POSTs to the Main API.
 * Injectable so tests never hit the network.
 */
export type ApiPostFn = (
  path: string,
  body: Record<string, unknown>
) => Promise<unknown>;

/** Public surface of the analytics instance. */
export interface AppAnalytics {
  /**
   * Always true — the server-side proxy decides whether creds are present.
   * Kept for API compatibility with the ZOR interface.
   */
  readonly isEnabled: boolean;
  /**
   * Send an event.  No-op when consent is suppressed.
   * Fire-and-forget: errors are captured to Sentry, never thrown.
   */
  sendEvent(name: string, params: Record<string, unknown>): void;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create the app analytics instance.
 *
 * Usage (in app-analytics-init.tsx):
 *
 *   const analytics = createAppAnalytics({}, { userId, orgId });
 *   analytics.sendEvent('page_view', {});
 *
 * @param options  - Consent env + storage overrides; no GA4 creds here.
 * @param _dims    - Reserved (userId/orgId ignored — server reads from token).
 * @param fetcher  - Network function; defaults to the Main API via apiManager.
 */
export const createAppAnalytics = (
  options: AppAnalyticsOptions,
  _dims: { userId: string | null; orgId: string | null },
  fetcher?: ApiPostFn
): AppAnalytics => {
  const consentEnv = options.consentEnv ?? browserConsentEnv;
  const clientId = resolveClientId(options.storage ?? defaultStorage());

  // Default fetcher uses apiManager (Clerk-authed axios instance).
  // Lazy-imported to avoid circular deps + allow test injection.
  const doPost: ApiPostFn =
    fetcher ??
    (async (path, body) => {
      const { apiManager } = await import('@/helpers/api.manager');
      return apiManager.post(path, body);
    });

  const sendEvent = (name: string, params: Record<string, unknown>): void => {
    if (isAppTrackingSuppressed(consentEnv)) return;

    const body = buildBackendEventBody({
      clientId,
      eventName: name,
      params,
    });

    doPost(
      APP_ANALYTICS_ENDPOINT,
      body as unknown as Record<string, unknown>
    ).catch((err: unknown) => {
      // Fire-and-forget: report to Sentry so a silent outage is visible,
      // but never surface to the caller.
      captureException(err, { tags: { area: 'ga4-app' }, level: 'warning' });
    });
  };

  return {
    get isEnabled() {
      return true; // creds live server-side; client is always "enabled"
    },
    sendEvent,
  };
};
